import { Box, Stack } from '@mui/material'
import type { CustomerExistingValuation as ExistingValuation } from '@einwert/valuation-api-client'
import type { ExistingAsset } from '@einwert/valuation-api-client'
import { tokens, WhiteEmptyStateImage } from 'ComponentLibrary'
import { isEmpty } from 'lodash'
import { getAllValueReferences, getMarketAreaValue } from './value-reference'
import { ComparablesCategory } from './ComparablesCategory'
import { filterMarketDataByMarketDataType } from './market-data'
import { AssetPreview } from './Preview/AssetPreview'
import { Typography } from 'ComponentLibrary/src/piazza'
import { useTranslation } from 'react-i18next'

export const PREVIEW_WIDTH = '33%'

type ValuationComparablesProps = {
    asset?: ExistingAsset
    valuation?: ExistingValuation
}

export const ValuationComparables = ({ asset, valuation }: ValuationComparablesProps) => {
    const { t: tValuationComparables } = useTranslation('pages', {
        keyPrefix: 'assets.assetDetails.valuation.valuationMarketData.valuationComparables',
    })
    const { market_data, market_comparison } = valuation?.comparables ?? {}
    const marketAreaValue = valuation ? getMarketAreaValue(valuation) : undefined

    if (!asset || !valuation || !market_data || isEmpty(market_data) || isEmpty(market_comparison)) {
        return (
            <Stack
                direction="column"
                gap={tokens.SizingSpacing200}
                height="264px"
                alignItems="center"
                justifyContent="center"
                paddingTop={tokens.SizingSpacing200}
            >
                <WhiteEmptyStateImage height="120px" width="145px" />
                <Typography variant="textS" color={tokens.ColorTextDefault}>
                    {tValuationComparables('emptyStateTitle')}
                </Typography>
            </Stack>
        )
    }

    const valuationDate = valuation?.valuation_date
    const valueReferences = getAllValueReferences(valuation)

    return (
        <Stack direction="row" gap={tokens.SizingSpacing200} position="relative">
            <Stack direction="column" gap={tokens.SizingSpacing400} width={`calc(100% - ${PREVIEW_WIDTH} - ${tokens.SizingSpacing200})`}>
                <ComparablesCategory
                    marketDataType="market_value"
                    marketData={filterMarketDataByMarketDataType(market_data, 'market_value', tValuationComparables)}
                    marketComparisonItem={market_comparison.market_value}
                    valueReference={valueReferences}
                    marketAreaDefinition={
                        marketAreaValue ? tValuationComparables(`marketAreaDefinitions.${marketAreaValue.kind}`) : undefined
                    }
                    marketAreaReferenceUnit={
                        marketAreaValue ? tValuationComparables(`marketAreaReferenceUnits.${marketAreaValue.kind}`) : undefined
                    }
                    valuationDate={valuationDate}
                />
                <ComparablesCategory
                    marketDataType="yield_and_multiplier"
                    marketData={filterMarketDataByMarketDataType(market_data, 'yield_and_multiplier', tValuationComparables)}
                    marketComparisonItem={market_comparison.yield_and_multiplier}
                    valueReference={valueReferences}
                    valuationDate={valuationDate}
                />
                <ComparablesCategory
                    marketDataType="rent"
                    marketData={filterMarketDataByMarketDataType(market_data, 'rent', tValuationComparables)}
                    marketComparisonItem={market_comparison.rent}
                    valueReference={valueReferences}
                    valuationDate={valuationDate}
                />
            </Stack>
            <Box width={PREVIEW_WIDTH} height="fit-content" position="sticky" top={tokens.SizingSpacing200}>
                <AssetPreview asset={asset} valuation={valuation} />
            </Box>
        </Stack>
    )
}
